import { useTranslation } from "react-i18next";
import type { LapAlignmentQuality, LapComparisonSeries } from "@/types";
import {
  COMPARISON_COLOR,
  REFERENCE_COLOR,
  formatElapsedSeconds,
  formatMetres,
  formatSignedSeconds,
  normalizeSelection,
  sectionDeltaSeconds,
} from "./comparisonMath";

interface SectionReadoutProps {
  series: LapComparisonSeries;
  quality: LapAlignmentQuality;
  cursorIndex: number | null;
  selection: [number, number] | null;
  referenceLabel: string;
  comparisonLabel: string;
  onClearSelection: () => void;
  id?: string;
}

function deltaTone(seconds: number): string {
  if (seconds > 0.0005) return "text-red-300";
  if (seconds < -0.0005) return "text-green-300";
  return "text-slate-200";
}

/**
 * Numbers behind the shared cursor and the selected section. Keeps the two
 * different quantities apart: the cumulative gap at a point, and the time
 * gained or lost between two points.
 */
export default function SectionReadout({
  series,
  quality,
  cursorIndex,
  selection,
  referenceLabel,
  comparisonLabel,
  onClearSelection,
  id,
}: SectionReadoutProps) {
  const { t } = useTranslation("pages");
  const index =
    cursorIndex ?? (selection ? normalizeSelection(selection[0], selection[1])[1] : null);
  const normalized = selection ? normalizeSelection(selection[0], selection[1]) : null;

  return (
    <div className="space-y-4 text-sm" id={id} aria-live="polite">
      <div className="rounded-lg border border-[#1a1a2e] bg-[#0d0d16] p-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("lapComparison.readout.cursor")}
        </div>
        {index === null ? (
          <p className="mt-2 text-slate-500">{t("lapComparison.readout.hoverHint")}</p>
        ) : (
          <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 font-mono text-xs">
            <dt className="text-slate-500">{t("lapComparison.readout.position")}</dt>
            <dd className="text-right text-slate-200">{formatMetres(series.position[index])}</dd>
            <dt style={{ color: REFERENCE_COLOR }}>{referenceLabel}</dt>
            <dd className="text-right text-slate-200">
              {formatElapsedSeconds(series.referenceTime[index])}
            </dd>
            <dt style={{ color: COMPARISON_COLOR }}>{comparisonLabel}</dt>
            <dd className="text-right text-slate-200">
              {formatElapsedSeconds(series.comparisonTime[index])}
            </dd>
            <dt className="text-slate-500">{t("lapComparison.readout.cumulativeGap")}</dt>
            <dd className={`text-right font-semibold ${deltaTone(series.delta[index])}`}>
              {formatSignedSeconds(series.delta[index])}
            </dd>
          </dl>
        )}
      </div>

      <div className="rounded-lg border border-[#1a1a2e] bg-[#0d0d16] p-3">
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t("lapComparison.readout.section")}
          </div>
          {normalized && (
            <button
              type="button"
              onClick={onClearSelection}
              className="text-xs text-slate-400 hover:text-slate-200"
            >
              {t("lapComparison.chart.clearSelection")}
            </button>
          )}
        </div>
        {!normalized ? (
          <p className="mt-2 text-slate-500">{t("lapComparison.chart.dragHint")}</p>
        ) : (
          <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 font-mono text-xs">
            <dt className="text-slate-500">{t("lapComparison.readout.range")}</dt>
            <dd className="text-right text-slate-200">
              {t("lapComparison.readout.sectionRange", {
                start: Math.round(series.position[normalized[0]]),
                end: Math.round(series.position[normalized[1]]),
              })}
            </dd>
            <dt className="text-slate-500">{t("lapComparison.readout.sectionGain")}</dt>
            <dd
              className={`text-right font-semibold ${deltaTone(
                sectionDeltaSeconds(series.delta, normalized[0], normalized[1]),
              )}`}
              data-testid="section-delta"
            >
              {formatSignedSeconds(
                sectionDeltaSeconds(series.delta, normalized[0], normalized[1]),
              )}
            </dd>
            <dt className="text-slate-500">{t("lapComparison.readout.gapAtEnd")}</dt>
            <dd className={`text-right ${deltaTone(series.delta[normalized[1]])}`}>
              {formatSignedSeconds(series.delta[normalized[1]])}
            </dd>
          </dl>
        )}
      </div>

      <p className="text-xs leading-relaxed text-slate-500">
        {t("lapComparison.signHint")}
      </p>
      <p className="text-xs text-slate-600">
        {t("lapComparison.quality.summary", {
          length: Math.round(quality.pathLengthM),
          offset: quality.lateralOffsetP95M.toFixed(1),
        })}
      </p>
    </div>
  );
}
